import {
  CAMPAIGN_CONTACT_FIELD_KEYS,
  emptyCampaignVariableMapping,
  type CampaignContactFieldKey,
  type CampaignDto,
  type CampaignMessageCoverageEntry,
  type CampaignPersonalizationSnapshot,
  type CampaignRecipientListItem,
  type CampaignVariableMapping,
  type CampaignVariableMappingEntry,
  type WhatsAppConnectionResponse,
  type WhatsAppSelectedPhoneNumber,
  type WhatsAppTemplateDto,
} from "@multi-chat/shared";
import {
  compactTemplatePreviewText,
  isApprovedSendableTemplate,
} from "../inbox/template-picker.js";
import {
  slotStorageKey,
  type TemplateSendSlot,
} from "../inbox/template-send.js";

export const CAMPAIGN_MESSAGE_MISSING_PLACEHOLDER = "⟦missing⟧";

export type CampaignMessageSourceType = CampaignVariableMappingEntry["source"];

export type CampaignMessageDraft = {
  platformEndpointId: number | null;
  whatsappTemplateId: number | null;
  variableMapping: CampaignVariableMapping;
};

export function canOpenCampaignMessageStep(input: {
  snapshotStale: boolean;
  readyRecipientCount: number;
}): boolean {
  return !input.snapshotStale && input.readyRecipientCount > 0;
}

export function usableWhatsAppSenders(
  connection: Pick<WhatsAppConnectionResponse, "phoneNumbers"> | null | undefined,
): WhatsAppSelectedPhoneNumber[] {
  if (!connection) return [];
  return connection.phoneNumbers.filter((phone) => phone.status === "ACTIVE");
}

export function campaignApprovedTemplates(
  templates: readonly WhatsAppTemplateDto[],
): WhatsAppTemplateDto[] {
  return templates.filter(isApprovedSendableTemplate);
}

export function resolveMappingValue(
  entry: CampaignVariableMappingEntry,
  personalization: CampaignPersonalizationSnapshot,
): string | null {
  switch (entry.source) {
    case "CONTACT_FIELD": {
      const raw = personalization.contactFields[entry.field];
      if (raw == null) return null;
      const trimmed = raw.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
    case "UPLOAD_COLUMN": {
      const raw = personalization.uploadFields[entry.column];
      if (raw == null) return null;
      const trimmed = raw.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
    case "CONSTANT": {
      const trimmed = entry.value.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
    default: {
      const _exhaustive: never = entry;
      return _exhaustive;
    }
  }
}

export function buildPreviewValuesMap(
  mapping: CampaignVariableMapping,
  personalization: CampaignPersonalizationSnapshot | null,
  slots: readonly TemplateSendSlot[],
): {
  valuesBySlotKey: Map<string, string>;
  missingParameterKeys: string[];
} {
  const valuesBySlotKey = new Map<string, string>();
  const missingParameterKeys: string[] = [];
  const seenMissing = new Set<string>();

  for (const slot of slots) {
    const entry = mapping.variables[slot.parameterKey];
    const storageKey = slotStorageKey(slot);
    if (!entry || !personalization) {
      valuesBySlotKey.set(storageKey, CAMPAIGN_MESSAGE_MISSING_PLACEHOLDER);
      if (!seenMissing.has(slot.parameterKey)) {
        seenMissing.add(slot.parameterKey);
        missingParameterKeys.push(slot.parameterKey);
      }
      continue;
    }
    const resolved = resolveMappingValue(entry, personalization);
    if (resolved == null) {
      valuesBySlotKey.set(storageKey, CAMPAIGN_MESSAGE_MISSING_PLACEHOLDER);
      if (!seenMissing.has(slot.parameterKey)) {
        seenMissing.add(slot.parameterKey);
        missingParameterKeys.push(slot.parameterKey);
      }
      continue;
    }
    valuesBySlotKey.set(storageKey, resolved);
  }

  return { valuesBySlotKey, missingParameterKeys };
}

export function compatibleMappingAfterTemplateChange(
  oldMapping: CampaignVariableMapping,
  newRequiredKeys: readonly string[],
): CampaignVariableMapping {
  const next: Record<string, CampaignVariableMappingEntry> = {};
  for (const key of newRequiredKeys) {
    const entry = oldMapping.variables[key];
    if (entry) {
      next[key] = entry;
    }
  }
  return { version: 1, variables: next };
}

export function uploadPersonalizationColumns(input: {
  upload?: {
    headers?: { headers: ReadonlyArray<{ key: string }> } | null;
    phoneColumnKey?: string | null;
  } | null;
  headers?: readonly string[] | null;
  recipients?: ReadonlyArray<
    Pick<CampaignRecipientListItem, "personalizationSnapshot">
  > | null;
}): string[] {
  const keys = new Set<string>();
  const phoneKey = input.upload?.phoneColumnKey ?? null;

  if (input.upload?.headers?.headers) {
    for (const header of input.upload.headers.headers) {
      if (phoneKey != null && header.key === phoneKey) continue;
      if (header.key.trim().length > 0) keys.add(header.key);
    }
  }

  if (input.headers) {
    for (const header of input.headers) {
      if (phoneKey != null && header === phoneKey) continue;
      if (header.trim().length > 0) keys.add(header);
    }
  }

  if (input.recipients) {
    for (const recipient of input.recipients) {
      for (const key of Object.keys(recipient.personalizationSnapshot.uploadFields)) {
        if (phoneKey != null && key === phoneKey) continue;
        if (key.trim().length > 0) keys.add(key);
      }
    }
  }

  return [...keys].toSorted((a, b) => a.localeCompare(b));
}

export function campaignContactFieldKeys(): readonly CampaignContactFieldKey[] {
  return CAMPAIGN_CONTACT_FIELD_KEYS;
}

export function contactFieldLabelKey(field: CampaignContactFieldKey): string {
  return `campaigns.message.fields.${field}`;
}

export function sourceTypeLabelKey(source: CampaignMessageSourceType): string {
  switch (source) {
    case "CONTACT_FIELD":
      return "campaigns.message.sources.contactField";
    case "UPLOAD_COLUMN":
      return "campaigns.message.sources.uploadedField";
    case "CONSTANT":
      return "campaigns.message.sources.fixedText";
    default: {
      const _exhaustive: never = source;
      return _exhaustive;
    }
  }
}

export function describeMappingEntry(entry: CampaignVariableMappingEntry): {
  source: CampaignMessageSourceType;
  detail: string;
} {
  switch (entry.source) {
    case "CONTACT_FIELD":
      return { source: entry.source, detail: entry.field };
    case "UPLOAD_COLUMN":
      return { source: entry.source, detail: entry.column };
    case "CONSTANT":
      return { source: entry.source, detail: entry.value };
    default: {
      const _exhaustive: never = entry;
      return _exhaustive;
    }
  }
}

/** Readable label for a mapping: localized Contact field name, CSV column, or the fixed text. */
export function mappingEntryLabel(
  t: (key: string) => string,
  entry: CampaignVariableMappingEntry,
): string {
  const described = describeMappingEntry(entry);
  if (described.source === "CONTACT_FIELD") {
    return t(contactFieldLabelKey(described.detail as CampaignContactFieldKey));
  }
  return described.detail;
}

export function draftFromCampaign(campaign: CampaignDto): CampaignMessageDraft {
  return {
    platformEndpointId: campaign.platformEndpointId,
    whatsappTemplateId: campaign.whatsappTemplateId,
    variableMapping: campaign.variableMapping ?? emptyCampaignVariableMapping(),
  };
}

export function isMessageDraftDirty(
  draft: CampaignMessageDraft,
  campaign: CampaignDto,
): boolean {
  if (draft.platformEndpointId !== campaign.platformEndpointId) return true;
  if (draft.whatsappTemplateId !== campaign.whatsappTemplateId) return true;
  return (
    JSON.stringify(draft.variableMapping) !== JSON.stringify(campaign.variableMapping)
  );
}

export function isMessageDraftReadyToSave(input: {
  draft: CampaignMessageDraft;
  requiredParameterKeys: readonly string[];
}): boolean {
  if (input.draft.platformEndpointId == null) return false;
  if (input.draft.whatsappTemplateId == null) return false;
  const variables = input.draft.variableMapping.variables;
  if (Object.keys(variables).length !== input.requiredParameterKeys.length) {
    return false;
  }
  return input.requiredParameterKeys.every((key) => variables[key] != null);
}

export function uniqueRequiredParameterKeys(
  slots: readonly TemplateSendSlot[],
): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const slot of slots) {
    if (seen.has(slot.parameterKey)) continue;
    seen.add(slot.parameterKey);
    keys.push(slot.parameterKey);
  }
  return keys;
}

export function formatSenderLabel(phone: WhatsAppSelectedPhoneNumber): string {
  const name = phone.displayName?.trim();
  if (name && name.length > 0) {
    return `${name} · ${phone.phoneNumber}`;
  }
  return phone.phoneNumber;
}

export function senderDisplayName(phone: WhatsAppSelectedPhoneNumber): string {
  const name = phone.displayName?.trim();
  return name && name.length > 0 ? name : phone.phoneNumber;
}

export function variableTokenLabel(parameterKey: string): string {
  return `{{${parameterKey}}}`;
}

export function excerptContainingVariable(
  body: string | null | undefined,
  parameterKey: string,
): string | null {
  if (body == null) return null;
  const collapsed = body.replace(/\s+/g, " ").trim();
  if (collapsed.length === 0) return null;
  const token = variableTokenLabel(parameterKey);
  const idx = collapsed.indexOf(token);
  if (idx < 0) {
    return compactTemplatePreviewText(collapsed, 72);
  }
  const start = Math.max(0, idx - 16);
  const end = Math.min(collapsed.length, idx + token.length + 28);
  let excerpt = collapsed.slice(start, end).trim();
  if (start > 0) excerpt = `…${excerpt}`;
  if (end < collapsed.length) excerpt = `${excerpt}…`;
  return excerpt;
}

const KNOWN_TEMPLATE_CATEGORIES = ["MARKETING", "UTILITY", "AUTHENTICATION"] as const;

export function campaignTemplateCategoryI18nKey(category: string): string {
  const normalized = category.trim().toUpperCase();
  if ((KNOWN_TEMPLATE_CATEGORIES as readonly string[]).includes(normalized)) {
    return `campaigns.message.categories.${normalized}`;
  }
  return "campaigns.message.categories.other";
}

export function campaignTemplateStatusI18nKey(status: string): string {
  const normalized = status.trim().toUpperCase();
  if (normalized === "APPROVED") {
    return "campaigns.message.statuses.APPROVED";
  }
  return "campaigns.message.statuses.other";
}

export function compactTemplateParts(components: unknown): {
  headerText: string | null;
  footerText: string | null;
  buttonCount: number;
} {
  let headerText: string | null = null;
  let footerText: string | null = null;
  let buttonCount = 0;
  if (!Array.isArray(components)) {
    return { headerText, footerText, buttonCount };
  }
  for (const entry of components) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const component = entry as Record<string, unknown>;
    const type = typeof component.type === "string" ? component.type.trim().toUpperCase() : "";
    if (type === "HEADER" && typeof component.text === "string") {
      const trimmed = component.text.trim();
      if (trimmed.length > 0) headerText = trimmed;
    }
    if (type === "FOOTER" && typeof component.text === "string") {
      const trimmed = component.text.trim();
      if (trimmed.length > 0) footerText = trimmed;
    }
    if (type === "BUTTONS" && Array.isArray(component.buttons)) {
      buttonCount += component.buttons.length;
    }
  }
  return { headerText, footerText, buttonCount };
}

export type MessageConfigurationBlocker =
  | "noSender"
  | "noTemplate"
  | "unsupported"
  | "unmapped"
  | "unsaved";

export function messageConfigurationBlocker(input: {
  hasSender: boolean;
  hasTemplate: boolean;
  discoveryKind: "supported" | "unsupported" | null;
  requiredParameterKeys: readonly string[];
  mappedKeyCount: number;
  dirty: boolean;
}): MessageConfigurationBlocker | null {
  if (!input.hasSender) return "noSender";
  if (!input.hasTemplate) return "noTemplate";
  if (input.discoveryKind === "unsupported") return "unsupported";
  if (input.discoveryKind !== "supported") return "noTemplate";
  if (input.mappedKeyCount !== input.requiredParameterKeys.length) return "unmapped";
  if (input.dirty) return "unsaved";
  return null;
}

export function messageConfigurationBlockerKey(
  blocker: MessageConfigurationBlocker,
): string {
  return `campaigns.message.blockers.${blocker}`;
}

/** Matches the server's coverage `mappedSource` format, so saved coverage is shown only for the same choice. */
export function mappingCoverageSourceKey(entry: CampaignVariableMappingEntry): string {
  switch (entry.source) {
    case "CONTACT_FIELD":
      return `CONTACT_FIELD:${entry.field}`;
    case "UPLOAD_COLUMN":
      return `UPLOAD_COLUMN:${entry.column}`;
    case "CONSTANT":
      return "CONSTANT";
    default: {
      const _exhaustive: never = entry;
      return _exhaustive;
    }
  }
}

export function mappingCoverageComplete(
  entry: CampaignMessageCoverageEntry | undefined,
): boolean {
  return entry != null && entry.totalReadyCount > 0 && entry.missingCount === 0;
}

export type TemplatePreviewSegment =
  | { kind: "text"; text: string }
  | {
      kind: "variable";
      parameterKey: string;
      display: string;
      mapped: boolean;
      componentType: "HEADER" | "BODY";
      occurrence: number;
    };

export type CampaignVariableTokenIdentity = {
  componentType: TemplateSendSlot["componentType"];
  parameterKey: string;
  buttonIndex?: number;
  occurrence?: number;
};

/** Stable DOM identity for one rendered token; the mapping itself stays keyed by parameterKey. */
export function campaignVariableTokenId(identity: CampaignVariableTokenIdentity): string {
  const button = identity.componentType === "BUTTON" ? `-${identity.buttonIndex ?? 0}` : "";
  return `${identity.componentType.toLowerCase()}${button}-${identity.parameterKey}-${identity.occurrence ?? 0}`;
}

export function configuredVariableCount(
  requiredKeys: readonly string[],
  mapping: CampaignVariableMapping,
): number {
  return requiredKeys.filter((key) => mapping.variables[key] != null).length;
}

export type CampaignVariableUsage = {
  componentType: TemplateSendSlot["componentType"];
  buttonIndex?: number;
};

export function parameterKeyUsages(
  slots: readonly TemplateSendSlot[],
  parameterKey: string,
): CampaignVariableUsage[] {
  const usages: CampaignVariableUsage[] = [];
  const seen = new Set<string>();
  for (const slot of slots) {
    if (slot.parameterKey !== parameterKey) continue;
    const id = slotStorageKey(slot);
    if (seen.has(id)) continue;
    seen.add(id);
    usages.push(
      slot.componentType === "BUTTON"
        ? { componentType: "BUTTON", buttonIndex: slot.buttonIndex ?? 0 }
        : { componentType: slot.componentType },
    );
  }
  return usages;
}

/** Required keys that have no interactive token in the rendered preview. */
export function unrenderedVariableKeys(input: {
  slots: readonly TemplateSendSlot[];
  headerFormat: "NONE" | "TEXT" | "IMAGE";
  headerText: string | null;
  bodyText: string | null;
}): string[] {
  const rendered = new Set<string>();
  for (const slot of input.slots) {
    if (slot.componentType === "HEADER") {
      if (input.headerFormat === "TEXT" && input.headerText) rendered.add(slot.parameterKey);
    } else if (slot.componentType === "BODY") {
      if (input.bodyText) rendered.add(slot.parameterKey);
    } else {
      rendered.add(slot.parameterKey);
    }
  }
  return uniqueRequiredParameterKeys(input.slots).filter((key) => !rendered.has(key));
}

export type CampaignContentHeaderStatus = {
  tone: "ready" | "incomplete";
  unsaved: boolean;
};

export function campaignContentHeaderStatus(input: {
  messageConfigured: boolean;
  draftDirty: boolean;
}): CampaignContentHeaderStatus {
  return {
    tone: input.messageConfigured && !input.draftDirty ? "ready" : "incomplete",
    unsaved: input.draftDirty,
  };
}

const TEMPLATE_TOKEN_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

export function splitTemplatePreviewSegments(input: {
  text: string;
  componentType: "HEADER" | "BODY";
  slots: readonly TemplateSendSlot[];
  valuesBySlotKey: ReadonlyMap<string, string>;
}): TemplatePreviewSegment[] {
  const relevant = new Map(
    input.slots
      .filter((slot) => slot.componentType === input.componentType)
      .map((slot) => [slot.parameterKey, slot]),
  );
  const parts: TemplatePreviewSegment[] = [];
  const occurrences = new Map<string, number>();
  const pattern = new RegExp(TEMPLATE_TOKEN_RE.source, "g");
  let lastIndex = 0;
  let match: RegExpExecArray | null = pattern.exec(input.text);
  while (match != null) {
    const parameterKey = match[1] ?? "";
    if (match.index > lastIndex) {
      parts.push({ kind: "text", text: input.text.slice(lastIndex, match.index) });
    }
    const slot = relevant.get(parameterKey);
    if (slot == null) {
      parts.push({ kind: "text", text: match[0] });
    } else {
      const value = input.valuesBySlotKey.get(slotStorageKey(slot)) ?? "";
      const mapped = value.trim().length > 0 && value !== CAMPAIGN_MESSAGE_MISSING_PLACEHOLDER;
      const occurrence = occurrences.get(parameterKey) ?? 0;
      occurrences.set(parameterKey, occurrence + 1);
      parts.push({
        kind: "variable",
        parameterKey,
        display: mapped ? value : `{{${parameterKey}}}`,
        mapped,
        componentType: input.componentType,
        occurrence,
      });
    }
    lastIndex = match.index + match[0].length;
    match = pattern.exec(input.text);
  }
  if (lastIndex < input.text.length) {
    parts.push({ kind: "text", text: input.text.slice(lastIndex) });
  }
  return parts;
}
